import React from 'react';
import { Card, CardContent } from '../ui/card';

interface LoadingStateProps {
  message?: string;
  size?: 'sm' | 'md' | 'lg';
  showCard?: boolean;
  progress?: number; // 0-100 for progress bar
  subMessage?: string; // Secondary message below main message
  variant?: 'spinner' | 'dots' | 'pulse' | 'progress';
  icon?: React.ReactNode; // Custom loading icon
}

export function LoadingState({ 
  message = "Loading...", 
  size = 'md',
  showCard = true,
  progress,
  subMessage,
  variant = 'spinner',
  icon
}: LoadingStateProps) {
  const sizeClasses = {
    sm: 'h-6 w-6',
    md: 'h-8 w-8', 
    lg: 'h-12 w-12'
  };

  const paddingClasses = {
    sm: 'py-6',
    md: 'py-12',
    lg: 'py-16'
  };

  const textSizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg'
  };

  const renderLoadingIcon = () => {
    if (icon) {
      return <div className={sizeClasses[size]}>{icon}</div>;
    }

    switch (variant) {
      case 'dots':
        return (
          <div className="flex space-x-1">
            <div className={`${sizeClasses[size]} bg-blue-600 rounded-full animate-bounce`} style={{ animationDelay: '0ms' }}></div>
            <div className={`${sizeClasses[size]} bg-blue-600 rounded-full animate-bounce`} style={{ animationDelay: '150ms' }}></div>
            <div className={`${sizeClasses[size]} bg-blue-600 rounded-full animate-bounce`} style={{ animationDelay: '300ms' }}></div>
          </div>
        );
      case 'pulse':
        return <div className={`${sizeClasses[size]} bg-blue-600 rounded-full animate-pulse`}></div>;
      case 'progress':
        return (
          <div className="w-16 h-16 relative">
            <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 64 64">
              <circle
                cx="32" cy="32" r="28"
                className="stroke-gray-200"
                strokeWidth="4"
                fill="none"
              />
              <circle
                cx="32" cy="32" r="28"
                className="stroke-blue-600"
                strokeWidth="4"
                fill="none"
                strokeDasharray={`${2 * Math.PI * 28}`}
                strokeDashoffset={`${2 * Math.PI * 28 * (1 - (progress || 0) / 100)}`}
                strokeLinecap="round"
                style={{ transition: 'stroke-dashoffset 0.3s ease' }}
              />
            </svg>
            {progress !== undefined && (
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-medium text-blue-600">{Math.round(progress)}%</span>
              </div>
            )}
          </div>
        );
      case 'spinner':
      default:
        return <div className={`animate-spin rounded-full ${sizeClasses[size]} border-b-2 border-blue-600`}></div>;
    }
  };

  const content = (
    <div className={`flex flex-col justify-center items-center ${paddingClasses[size]}`}>
      <div className="flex items-center mb-2">
        {renderLoadingIcon()}
        <span className={`ml-3 text-gray-600 ${textSizeClasses[size]} font-medium`}>{message}</span>
      </div>
      
      {subMessage && (
        <p className="text-gray-500 text-sm text-center max-w-md">{subMessage}</p>
      )}
      
      {variant === 'progress' && progress !== undefined && (
        <div className="w-full max-w-xs mt-4">
          <div className="bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      )}
    </div>
  );

  if (!showCard) {
    return content;
  }

  return (
    <Card>
      <CardContent className="p-0">
        {content}
      </CardContent>
    </Card>
  );
}